/**
 * Builds a complete frozen snapshot (FinalValues) of all computed invoice data.
 * This snapshot captures everything needed to render a finalized invoice
 * (UI display + PDF generation) without any live entity lookups.
 */

import type {
    FinalValues,
    FinalTask,
    FinalExpense,
    FinalFlatFee,
    FinalFlatFeeMilestone,
    FinalDiscount,
    FinalTransaction,
    FinalTrustAccounting,
    FinalTrustInfo,
    FinalDisplayMetadata,
} from '../../@types/invoice';
import type Invoice from '../../entities/invoice';
import type User from '../../entities/user';
import type Contact from '../../entities/contact';
import type Transaction from '../../entities/transaction';
import type FlatFee from '../../entities/flatFee';
import FinancialHelper, { InvoiceInfo } from '../FinancialHelper';
import { formatFML, formatFMLFromId, formatUserName } from '../../Contacts/ContactFormat';
import AddressHelper from '../../Contacts/Address/AddressHelper';
import { getCost } from '../../TaskManagement/TaskHelper';
import { formatDuration, roundDurationToMinutes } from '../../lib/picos/duration';
import { getBillableRate } from '../../TaskManagement/TaskHelper';
import CaseHelper from '../../Case/CaseHelper';
import LookupRegistry from '../../ui/LookupSelect/LookupRegistry';
import OptionHelper from '../../Configuration/GeneralOptions/OptionHelper';
import { InterestMethods } from '../../Configuration/GeneralOptions/OptionConstants';
import { getCurrentFirm } from '../../lib/picos/currentLogin';
import { getEmails } from '../../lib/picos/getResourceTypes';
import * as DateUtil from '../../lib/dateutils';
import find from '../../store/find';
import store from '../../store/store';
import { toImageWidthPercent } from '../../lib/pdf/utils/parseQuillHtml';
import { leadPeriodText } from './leadPeriod';
import { frozenMilestonePercentage } from '../../Case/FlatFee/flatFeeHelpers';

/**
 * Transaction type labels for trust accounting
 */
const TX_TYPE_LABELS = new Map<string, string>([
    ['FR', 'Trust Deposit'],
    ['FO', 'Split Payment'],
    ['PT', 'Payment from Trust'],
    ['PC', 'Payment from Client'],
    ['ST', 'Manual Balance'],
    ['SA', 'Manual Balance'],
    ['TT', 'Trust Transfer'],
    ['DE', 'Escrow Distribution'],
    ['ED', 'Escrow Deposit'],
    ['RR', 'Refund Retainer'],
    ['DT', 'Trust Disbursement'],
    ['BA', 'Adjustment'],
]);

/**
 * Pre-resolve a transaction description so it doesn't require live entity lookups later.
 */
function describeTransaction(tr: Transaction): string {
    if (tr.txType === 'SA') return 'Balance Forward';
    else if (tr.txType === 'PT') {
        if (tr.sourceType === 'M') return 'Payment from Case Trust';
        else return 'Payment from Client Trust';
    }
    else if (tr.txType === 'PC') {
        const payerName = formatFMLFromId(tr.source!);
        return `Payment from ${payerName}`;
    }
    else if (['BA', 'ST'].includes(tr.txType)) return 'Adjustment';
    else if (tr.txType === 'TT') return 'Transfer to Trust';
    else return 'Payment';
}

/**
 * Get user initials from user ID, resolving via the Contact entity.
 */
function getUserInitials(userId: string | null | undefined): string {
    if (!userId) return '';
    const user = find<User>('users', userId);
    if (!user) return '';

    if (user.contactId) {
        const contact = find<Contact>('contacts', user.contactId);
        if (contact) {
            let initials = '';
            if (contact.first) initials += contact.first[0];
            if (contact.middle) initials += contact.middle[0];
            if (contact.last) initials += contact.last[0];
            if (initials) return initials.toUpperCase();
        }
    }

    const name = formatUserName(user);
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
}

/**
 * Get the display name for a user from their userId.
 */
function getUserName(userId: string | null | undefined): string {
    if (!userId) return '';
    const user = find<User>('users', userId);
    if (!user) return '';
    return formatUserName(user);
}

/**
 * Format a date for snapshot storage (ISO string or null).
 */
function toDateString(date: Date | string | null | undefined): string | null {
    if (!date) return null;
    if (date instanceof Date) return date.toISOString();
    return String(date);
}

/**
 * Build a complete FinalValues snapshot from an invoice and its computed InvoiceInfo.
 */
export function buildFinalValues(invoice: Invoice, info: InvoiceInfo): FinalValues {
    // Snapshot tasks
    const tasks: FinalTask[] = info.tasks.map(task => ({
        taskId: task.id,
        title: task.title || '',
        invoiceNotes: task.invoiceNotes || null,
        completionDate: toDateString(task.completionDate),
        assignedToId: task.assignedTo || task.createdBy || null,
        assignedToName: getUserName(task.assignedTo || task.createdBy),
        assignedToInitials: getUserInitials(task.assignedTo || task.createdBy),
        elapsedTime: task.elapsedTime || 0,
        hours: (() => {
            const billingIncrement = invoice.billingIncrement ?? new OptionHelper().getOptionValue(OptionHelper.BILLING_INCREMENT) as number;
            const roundingMethod = invoice.roundingMethod ?? new OptionHelper().getOptionValue(OptionHelper.ROUNDING_METHOD) as string;
            return roundDurationToMinutes(task.elapsedTime, billingIncrement, roundingMethod) / 3_600_000;
        })(),
        rate: task.associatedRate || 0,
        effectiveRate: task.isLaborRateOverridden ? task.laborRateOverride || 0 : task.laborRate || task.associatedRate || 0,
        cost: getCost(task, invoice),
        noCharge: !!task.noCharge,
        hideFromInvoice: !!task.hideFromInvoice,
        overrideHours: task.laborTimeOverride ?? null,
        overrideRate: task.laborRateOverride ?? null,
        overrideCost: task.costOverride ?? null,
        overrideNoCharge: task.isCostOverridden ? true : null,
        flatFeeBucket: task.flatFeeBucket || null,
        flatFeeBucketName: task.flatFeeBucket ? find<FlatFee>('flatFees', task.flatFeeBucket)?.name ?? null : null,
        activityCode: task.activityTypeIds?.[0] || null,
        notification: task.notification || null,
    }));

    // Snapshot expenses
    const expenses: FinalExpense[] = info.expenses.map(expense => ({
        expenseId: expense.id,
        description: expense.description || null,
        expenseDate: toDateString(expense.expenseDate),
        cost: expense.cost || 0,
        quantity: expense.quantity || 1,
        total: (expense.quantity || 1) * (expense.cost || 0),
        payeeName: expense.payee ? formatFMLFromId(expense.payee, false) : null,
        reimbursable: !!expense.reimbursable,
        taxable: !!expense.taxable,
        expenseType: expense.expenseType || null,
    }));

    // Snapshot flat fees
    const flatFees: FinalFlatFee[] = info.flatFeeInfo.flatFees.map(ff => ({
        flatFeeId: ff.id,
        name: ff.name || '',
        description: ff.description || null,
        amount: ff.amount || 0,
        earnedThisPeriod: !!ff.earnedThisPeriod,
        earnedThisPeriodAmount: ff.earnedThisPeriod || 0,
        completionDate: toDateString(ff.completionDate),
        position: ff.position ?? null,
    }));

    // Snapshot flat fee milestones
    const flatFeeMilestones: FinalFlatFeeMilestone[] = info.flatFeeInfo.flatFeeMilestones.map(ffm => ({
        flatFeeMilestoneId: ffm.id,
        name: ffm.name || '',
        description: ffm.description || null,
        amount: ffm.amount || 0,
        // The frozen percentage is a 0 to 1 fraction; the milestone holds 0 to 100 (OWA-1595)
        percentage: frozenMilestonePercentage(ffm.percentage),
        earnedThisPeriod: !!ffm.earnedThisPeriod,
        flatFeeId: ffm.entityId || null,
        flatFeeName: ffm.entityId ? find<FlatFee>('flatFees', ffm.entityId)?.name ?? null : null,
        earnedThisPeriodAmount: ffm.earnedThisPeriod || 0,
        completionDate: toDateString(ffm.completionDate),
        position: ffm.position ?? null,
    }));

    // Snapshot discounts with pre-resolved display names
    const discounts: FinalDiscount[] = info.discounts.map(discount => ({
        discountId: discount.id,
        reason: discount.reason || null,
        reasonDisplay: LookupRegistry.getLookupById(discount.reason)?.key || discount.reason || '',
        amount: discount.amount || 0,
        method: discount.method || 'S',
        percentage: discount.percentage ?? null,
        simple: discount.simple ?? null,
        applyToLabor: !!discount.applyToLabor,
        applyToExpenses: !!discount.applyToExpenses,
        laborAmount: discount.laborAmount || 0,
        expenseAmount: discount.expenseAmount || 0,
    }));

    // Snapshot transaction helper
    function snapshotTransaction(tr: Transaction): FinalTransaction {
        return {
            transactionId: tr.id,
            txType: tr.txType,
            txDate: toDateString(tr.txDate),
            amount: tr.amount || 0,
            notes: tr.notes || null,
            sourceType: tr.sourceType || null,
            source: tr.source || null,
            description: describeTransaction(tr),
        };
    }

    const payments: FinalTransaction[] = info.payments
        .filter(tx => !tx.voidedDate && !tx.refusedDate && tx.status !== 'D')
        .map(snapshotTransaction);
    const adjustments: FinalTransaction[] = info.adjustments.map(snapshotTransaction);
    const withdrawals: FinalTransaction[] = info.withdrawals.map(snapshotTransaction);
    const refunds: FinalTransaction[] = info.refunds.map(snapshotTransaction);

    // Snapshot trust accounting
    const trustAccounting = buildTrustAccountingSnapshot(invoice);
    const fundingMatter = !invoice.leadId && invoice.matterIds.length === 1
        ? CaseHelper.getMatterFromId(invoice.matterIds[0]) : null;
    // Use the same as-of balance that TrustBalanceInput used to set the
    // invoice's request. The display-oriented trustAccounting snapshot can
    // differ from that balance, so it is not a safe payment target baseline.
    const fundingRequest = invoice.additionalFundingNeeded ?? 0;
    const trustFundingTarget = fundingRequest > 0
        ? fundingMatter
            ? FinancialHelper.getTrustAmounts(fundingMatter, invoice.issueDate).totalTrust + fundingRequest
            : undefined
        : null;

    // Snapshot display metadata
    const display = buildDisplayMetadata(invoice, info);

    return {
        // Invoice dates
        issueDate: toDateString(invoice.issueDate),
        dueDate: toDateString(invoice.dueDate),

        // Computed totals
        taskTotal: info.taskTotal,
        expenseTotal: info.expenseTotal,
        flatFeeTotal: info.flatFeeTotal,
        discountTotal: info.discountTotal,
        adjustmentTotal: info.adjustmentTotal,
        refundTotal: info.refundTotal,
        withdrawalsTotal: info.withdrawalsTotal,
        paymentTotal: info.paymentTotal,
        transactionTotal: info.transactionTotal,
        currentInvoiceAmount: info.currentInvoiceAmount,
        pastDue: info.pastDue,
        currentBalance: info.currentBalance,
        amountDue: info.amountDue,
        isFirstInvoice: info.isFirstInvoice,

        // Tax and interest computed totals
        taxTotal: info.taxTotal,
        interestTotal: info.interestTotal,

        // Tax configuration (frozen at finalization)
        taxRate: OptionHelper.getOptionValueStatic(OptionHelper.INVOICE_TAX_RATE) as number || 0,
        taxLabel: OptionHelper.getOptionValueStatic(OptionHelper.INVOICE_TAX_LABEL) as string || '',

        // Interest configuration (frozen at finalization)
        interestRate: OptionHelper.getOptionValueStatic(OptionHelper.INVOICE_INTEREST_RATE) as number || 0,
        interestMethod: OptionHelper.getOptionValueStatic(OptionHelper.INVOICE_INTEREST_METHOD) as string || InterestMethods.SIMPLE_MONTHLY,
        interestGracePeriod: OptionHelper.getOptionValueStatic(OptionHelper.INVOICE_INTEREST_GRACE_PERIOD) as number || 0,

        // Line items
        tasks,
        expenses,
        flatFees,
        flatFeeMilestones,
        discounts,

        // Transactions
        payments,
        adjustments,
        withdrawals,
        refunds,

        // Trust
        trustAccounting,
        trustFundingTarget,

        // Display
        display,
    };
}

/**
 * Build trust accounting snapshot from live data.
 */
function buildTrustAccountingSnapshot(invoice: Invoice): FinalTrustAccounting {
    const isLead = !!invoice.leadId;
    if (isLead || !invoice.matterIds?.length) {
        return { caseTrust: null, clientTrusts: [] };
    }

    const matter = CaseHelper.getMatterFromId(invoice.matterIds[0]);
    if (!matter) {
        return { caseTrust: null, clientTrusts: [] };
    }

    // Case trust
    const matterTrustInfo = FinancialHelper.getMatterTrustInfo(matter.id, invoice);
    const caseTrust: FinalTrustInfo = {
        id: matter.id,
        name: matter.name || 'Case Trust',
        beginningBalance: matterTrustInfo.startingBalance,
        endingBalance: matterTrustInfo.endingBalance,
        transactions: matterTrustInfo.transactionsThisPeriod.map(tr => ({
            date: toDateString(tr.txDate),
            type: tr.txType,
            typeLabel: TX_TYPE_LABELS.get(tr.txType) || tr.txType,
            notes: tr.notes || null,
            amount: FinancialHelper.getPaymentAmount(tr, matter.id),
            runningBalance: tr.currentBalance || 0,
        })),
    };

    // Client trusts
    const clientTrusts: FinalTrustInfo[] = invoice.clientIds
        .map(cid => {
            const clientInfo = FinancialHelper.getClientTrustInfo(cid, invoice);
            if (!clientInfo) return null;

            return {
                id: cid,
                name: formatFMLFromId(cid),
                beginningBalance: clientInfo.startingBalance,
                endingBalance: clientInfo.endingBalance,
                transactions: clientInfo.transactionsThisPeriod.map((tr: Transaction) => ({
                    date: toDateString(tr.txDate),
                    type: tr.txType,
                    typeLabel: TX_TYPE_LABELS.get(tr.txType) || tr.txType,
                    notes: tr.notes || null,
                    amount: FinancialHelper.getPaymentAmount(tr, invoice.matterIds[0]),
                    runningBalance: tr.currentBalance || 0,
                })),
            } as FinalTrustInfo;
        })
        .filter(Boolean) as FinalTrustInfo[];

    return { caseTrust, clientTrusts };
}

/**
 * Build display metadata snapshot.
 */
function buildDisplayMetadata(invoice: Invoice, info: InvoiceInfo): FinalDisplayMetadata {
    const optionHelper = new OptionHelper();

    // Resolve client display info
    const clients = invoice.clientIds.map(cid => {
        const contact = find<Contact>('contacts', cid);
        if (!contact) return null;

        const address = AddressHelper.getDeliveryAddress([contact]);
        const addressStr = address ? AddressHelper.convertAddressToParagraph(address, false) as string : null;
        const emails = getEmails(contact).filter(e => e.isPrimary && e.canContact);

        return {
            clientId: cid,
            name: formatFML(contact),
            billingName: contact.billingName || null,
            address: addressStr || null,
            email: emails[0]?.value || null,
            phone: null, // Could be expanded if needed
        };
    }).filter(Boolean) as FinalDisplayMetadata['clients'];

    // Resolve matter display info
    const matters = (invoice.matterIds || []).map(mid => {
        const matter = CaseHelper.getMatterFromId(mid);
        return matter ? { matterId: mid, name: matter.name || '' } : null;
    }).filter(Boolean) as FinalDisplayMetadata['matters'];

    // Per-case bill-to contact snapshot (OWA-320): if the invoice's (primary) matter
    // designates a third-party bill-to contact, freeze its display name + delivery
    // address so the finalized invoice is addressed to it instead of the client(s).
    const primaryMatter = invoice.matterIds?.[0] ? CaseHelper.getMatterFromId(invoice.matterIds[0]) : null;
    let billTo: FinalDisplayMetadata['billTo'] = null;
    if (primaryMatter?.billToContactId) {
        const billToContact = find<Contact>('contacts', primaryMatter.billToContactId);
        if (billToContact) {
            const billToAddr = AddressHelper.getDeliveryAddress([billToContact]);
            const billToAddrStr = billToAddr
                ? AddressHelper.convertAddressToParagraph(billToAddr, false) as string
                : null;
            billTo = {
                contactId: billToContact.id,
                name: billToContact.billingName || formatFML(billToContact),
                address: billToAddrStr || null,
            };
        }
    }

    // Lead name
    const lead = invoice.leadId ? store.getState().leads.get(invoice.leadId) : null;
    const leadName = lead?.name || null;

    // Firm info
    const firm = getCurrentFirm();
    const firmLetterhead = optionHelper.getOptionValue<string>(OptionHelper.FIRM_LETTERHEAD) || null;
    const letterheadWidthPercent = toImageWidthPercent(optionHelper.getOptionValue(OptionHelper.LETTERHEAD_WIDTH));

    // Court time by resource
    const courtTimeByResource = buildCourtTimeByResource(invoice, info);

    return {
        clients,
        matters,
        billTo,
        leadName,
        firmName: firm.legalName || '',
        firmLetterhead,
        ...(letterheadWidthPercent != null && {letterheadWidthPercent}),
        courtTimeByResource,
        letterheadCssUnits: true,
        fontSize: optionHelper.getOptionValue<string>(OptionHelper.FONT_SIZE) || '10pt',
        consultName: optionHelper.getOptionValue<string>(OptionHelper.PAID_CONSULT_NAME) || 'Consultation',
        narrativeHeader: optionHelper.getOptionValue<string>(OptionHelper.INVOICE_NARRATIVE_HEADER) || 'Work We Did for You',
        billingIncrement: invoice.billingIncrement,
        roundingMethod: invoice.roundingMethod,
        // The Period a lead invoice shows, fixed at finalization (OWA-987).
        ...(invoice.leadId && {
            leadPeriod: leadPeriodText(optionHelper.getOptionValue<string>(OptionHelper.INVOICE_LEAD_PERIOD),
                invoice, lead?.initialConsultDate),
        }),
    };
}

/**
 * Build court time breakdown by resource (for court invoices).
 */
function buildCourtTimeByResource(invoice: Invoice, info: InvoiceInfo): FinalDisplayMetadata['courtTimeByResource'] {
    const billingIncrement = invoice.billingIncrement ?? new OptionHelper().getOptionValue(OptionHelper.BILLING_INCREMENT) as number;
    const roundingMethod = invoice.roundingMethod ?? new OptionHelper().getOptionValue(OptionHelper.ROUNDING_METHOD) as string;

    const filteredTasks = info.tasks.filter(t => !t.hideFromInvoice);
    const userIds = [...new Set(filteredTasks.map(t => t.assignedTo))];

    return userIds.map(uid => {
        const userName = getUserName(uid);
        const hours = filteredTasks
            .filter(t => t.assignedTo === uid)
            .reduce((sum, t) => sum + roundDurationToMinutes(t.elapsedTime, billingIncrement, roundingMethod) / 3_600_000, 0);

        return { userName, hours: +hours.toFixed(2) };
    });
}

export default buildFinalValues;
